import React, { useState, useEffect, useMemo } from 'react';
import { CenteredModal } from '~/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faPlus, faShieldAlt, faCheck, faChevronDown, faChevronRight, faMinusSquare, faUser } from '@fortawesome/free-solid-svg-icons';
import RolePermissionAPI from '~/api/rolePermissionApi';
import '~/pages/authentications/auth.css';

function RoleManagementModal({ show, onClose }) {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    const [roles, setRoles] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    
    const [selectedRole, setSelectedRole] = useState(null);
    const [rolePermissions, setRolePermissions] = useState([]);
    
    const [usersInRole, setUsersInRole] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [showUserPicker, setShowUserPicker] = useState(false);

    const [newRoleName, setNewRoleName] = useState('');
    const [isCreatingRole, setIsCreatingRole] = useState(false);
    
    const [openGroups, setOpenGroups] = useState({});

    const toggleGroup = (group) => {
        setOpenGroups(prev => {
            const currentState = prev[group] !== false;
            return {
                ...prev,
                [group]: !currentState
            };
        });
    };

    useEffect(() => {
        if (show) {
            fetchInitialData();
        } else {
            // reset state
            setSelectedRole(null);
            setRolePermissions([]);
            setUsersInRole([]);
            setSelectedUserId('');
            setShowUserPicker(false);
            setMessage('');
        }
    }, [show]);

    useEffect(() => {
        if (message && messageType === 'success') {
            const timer = setTimeout(() => {
                setMessage('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message, messageType]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [rolesRes, permsRes] = await Promise.all([
                RolePermissionAPI.getRoles(),
                RolePermissionAPI.getAllPermissions()
            ]);

            if (rolesRes.data?.Succeeded) {
                const fetchedRoles = rolesRes.data.Data || [];
                setRoles(fetchedRoles.filter(r => r.Name !== 'User'));
            }
            if (permsRes.data?.Succeeded) {
                setAllPermissions(permsRes.data.Data || []);
            }
        } catch (error) {
            setMessageType('danger');
            setMessage('Failed to load roles and permissions.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectRole = async (role) => {
        setSelectedRole(role);
        setSelectedUserId('');
        setMessage('');
        try {
            setLoading(true);
            const [permsRes, usersRes] = await Promise.all([
                RolePermissionAPI.getRolePermissions(role.Id),
                RolePermissionAPI.getUsersInRole(role.Id)
            ]);
            
            if (permsRes.data?.Succeeded) {
                setRolePermissions(permsRes.data.Data || []);
            }
            if (usersRes.data?.Succeeded) {
                setUsersInRole(usersRes.data.Data || []);
            }
        } catch (error) {
            setMessageType('danger');
            setMessage('Failed to load permissions or users for role.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectUser = async (userId) => {
        setSelectedUserId(userId);
        setMessage('');
        if (!selectedRole) return;

        try {
            setLoading(true);
            if (!userId) {
                // If "Entire Role" selected
                const res = await RolePermissionAPI.getRolePermissions(selectedRole.Id);
                if (res.data?.Succeeded) {
                    setRolePermissions(res.data.Data || []);
                }
            } else {
                // Specific user
                const res = await RolePermissionAPI.getUserPermissions(userId);
                if (res.data?.Succeeded) {
                    setRolePermissions(res.data.Data || []);
                }
            }
        } catch (error) {
            setMessageType('danger');
            setMessage('Failed to load permissions.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRole = async () => {
        if (!newRoleName.trim()) {
            setMessageType('danger');
            setMessage('Role name cannot be empty.');
            return;
        }
        try {
            setIsCreatingRole(true);
            const res = await RolePermissionAPI.createRole({ RoleName: newRoleName });
            if (res.data?.Succeeded) {
                setNewRoleName('');
                setMessageType('success');
                setMessage(res.data?.Message || 'Role created successfully.');
                fetchInitialData(); // Refresh roles
            } else {
                setMessageType('danger');
                setMessage(res.data?.Message || 'Failed to create role.');
            }
        } catch (error) {
            setMessageType('danger');
            setMessage(error.response?.data?.Message || 'Failed to create role.');
        } finally {
            setIsCreatingRole(false);
        }
    };

    const handleTogglePermission = (permissionId) => {
        if (selectedRole?.Name === 'Super Admin') return;

        setRolePermissions(prev => {
            if (prev.includes(permissionId)) {
                let newPerms = prev.filter(p => p !== permissionId);

                // If removing an '.Access' permission, also remove all dependent actions for this module
                if (permissionId.endsWith('.Access')) {
                    const modulePrefix = permissionId.replace('.Access', '.');
                    newPerms = newPerms.filter(p => !p.startsWith(modulePrefix));
                }

                return newPerms;
            } else {
                let newPerms = [...prev, permissionId];

                // If adding an action (e.g., .View), auto-grant the '.Access' permission if it exists
                if (!permissionId.endsWith('.Access') && permissionId.includes('.')) {
                    const modulePrefix = permissionId.substring(0, permissionId.lastIndexOf('.'));
                    const accessPermId = `${modulePrefix}.Access`;
                    
                    if (!prev.includes(accessPermId) && allPermissions.includes(accessPermId)) {
                        newPerms.push(accessPermId);
                    }
                }

                return newPerms;
            }
        });
    };

    const handleToggleGroup = (groupPerms, isChecked) => {
        if (selectedRole?.Name === 'Super Admin') return;

        const permIds = groupPerms.map(p => p.id);
        setRolePermissions(prev => {
            if (isChecked) {
                // Add all permissions in group that aren't already included
                const newPerms = permIds.filter(id => !prev.includes(id));
                return [...prev, ...newPerms];
            } else {
                // Remove all permissions in group
                return prev.filter(p => !permIds.includes(p));
            }
        });
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!selectedRole) return;
        
        if (selectedRole.Name === 'Super Admin') {
            setMessageType('danger');
            setMessage('Cannot modify Super Admin permissions.');
            return;
        }

        try {
            setLoading(true);
            let res;
            if (!selectedUserId) {
                res = await RolePermissionAPI.updateRolePermissions(selectedRole.Id, { Permissions: rolePermissions });
            } else {
                res = await RolePermissionAPI.updateUserPermissions(selectedUserId, { Permissions: rolePermissions });
            }

            if (res.data?.Succeeded) {
                setMessageType('success');
                setMessage(res.data?.Message || 'Permissions saved successfully.');
            } else {
                setMessageType('danger');
                setMessage(res.data?.Message || 'Failed to save permissions.');
            }
        } catch (error) {
            setMessageType('danger');
            setMessage(error.response?.data?.Message || 'Failed to save permissions.');
        } finally {
            setLoading(false);
        }
    };

    const groupedPermissions = useMemo(() => {
        const getGroupPath = (rawModule) => {
            const map = {
                'DashboardAnalytics': ['Dashboard', 'Analytics'],
                'DashboardMetrics': ['Dashboard', 'Metrics'],
                'UserRole': ['User Management', 'User Role'],
                // 'SecuritySettings': ['User Management', 'Security Settings'],
                'FileManager': ['File Management', 'Image Gallery'],
                'AssetsImage': ['File Management', 'Assets Image'],
                'FileImageUrl': ['File Management', 'Image Url'],
                'PaymentInformation': ['Payment & Subscription', 'Payment Information'],
                'StripePayments': ['Payment & Subscription', 'Stripe Payments'],
                'LandingPageAboutUs': ['Landing Page', 'About Us'],
                'LandingPageOurService': ['Landing Page', 'Our Service'],
                'LandingPagePrivateBlogPost': ['Landing Page', 'Private Blog Post'],
                'LandingPageRoute': ['Landing Page', 'Route'],
                'LandingPageArticle': ['Landing Page', 'Article'],
                'LandingPageTerms': ['Landing Page', 'Terms'],
                'LandingPageHome': ['Landing Page', 'Home'],
                'Appointment': ['System Management', 'General', 'Appointment'],
                'ApplicationParam': ['System Management', 'General', 'Application Parameter'],
                'SupportTicket': ['AI Chatbot Page', 'Support Ticket History'],
                'Notification': ['System Management', 'General', 'Notification'],
                'SystemJob': ['System Management', 'General', 'System Jobs'],
                // 'SystemSettings': ['System Management', 'General', 'System Settings'],
                'DatabaseManagement': ['System Management', 'Database', 'Database Management'],
                'EmailTemplate': ['System Management', 'Email & Messaging', 'Email Template'],
                'SmtpSettings': ['System Management', 'Email & Messaging', 'SMTP Settings'],
                'Mailjet': ['System Management', 'Email & Messaging', 'Mailjet'],
                'NotiInAppTemplate': ['System Management', 'Email & Messaging', 'In-App Notification Template'],
                'AuditLog': ['System Management', 'Logs Management', 'Audit Log'],
                'SystemException': ['System Management', 'Logs Management', 'System Exception'],
                'FileManager': ['File Management', 'Image Gallery'],
                'AssetsImage': ['File Management', 'Assets Image'],
                'FileImageUrl': ['File Management', 'Image Url'],
                'UserVisitor': ['User Management', 'User Visitor'],
                'UserInformation': ['User Management', 'User Information'],
                'UserPlanSubscription': ['Payment & Subscription', 'User Plan Subscription'],
                'ServiceManager': ['Payment & Subscription', 'Service Manager'],
                'UserCardManagement': ['Payment & Subscription', 'User Card Management'],
                'ChatbotManagement': ['AI Chatbot Page', 'Chatbot Management'],
                'ChatbotFeedback': ['AI Chatbot Page', 'Chat Feedback'],
                'AwsChatbot': ['AI Chatbot Page', 'Manage Chat Sessions'],
                'QdrantSync': ['AI Chatbot Page', 'Qdrant Vector Sync'],
                'AiServicesUsage': ['System Management', 'AI Services', 'Usage'],
                'AiServicesPricing': ['System Management', 'AI Services', 'OpenAI Pricing'],
                'AiServicesMasterData': ['System Management', 'AI Services', 'Master Data'],
                'AiServicesTrafficAnalytics': ['System Management', 'AI Services', 'Traffic Analytics'],
                'ApiMonitoring': ['System Management', 'API Monitoring & Testing'],
                'HeaderDatabaseManagement': ['Header', 'Database Management'],
                'HeaderSystemServicesTesting': ['Header', 'System Services Testing'],
                'HeaderSecuritySettings': ['Header', 'Security Settings'],
                'HeaderSearchSettings': ['Header', 'Search Settings'],
                'HeaderCreateUserAccount': ['Header', 'Create User Account'],
                'SecuritySettings': ['Header', 'Security Settings']
            };
            if (map[rawModule]) return map[rawModule];
            
            if (rawModule.startsWith('LandingPage')) return ['Landing Page', rawModule.replace('LandingPage', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Dashboard')) return ['Dashboard', rawModule.replace('Dashboard', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Aws')) return ['AWS', rawModule.replace('Aws', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            if (rawModule.startsWith('Chatbot')) return ['Chatbot', rawModule.replace('Chatbot', '').replace(/([a-z])([A-Z])/g, '$1 $2')];
            
            return ['Other', rawModule.replace(/([a-z])([A-Z])/g, '$1 $2')];
        };

        const root = {};
        allPermissions
            .filter(p => !p.includes('Header.CreateAdminAccount') && !p.includes('Header.RoleManagement') && !p.includes('SystemSettings'))
            .forEach(p => {
            const parts = p.split('.');
            let rawModule = parts.length > 1 ? parts[1] : 'Other';
            if (rawModule === 'Header' && parts.length > 2) {
                rawModule = 'Header' + parts[2];
            }
            
            const path = getGroupPath(rawModule);
            const leafNodeName = path[path.length - 1];

            let displayAction = '';
            if (parts.length > 2) {
                let rawAction = parts[2].replace(/([a-z])([A-Z])/g, '$1 $2');
                if (rawModule === 'FileManager' && rawAction === 'Upload') {
                    rawAction = 'Upload & Rename';
                }
                if (rawModule === 'FileManager' && rawAction === 'Delete') {
                    rawAction = 'Delete & Bulk Delete';
                }
                if (rawModule === 'UserPlanSubscription' && rawAction === 'Assign Plan') {
                    rawAction = 'Assign Plan & Assign Specific User';
                }
                if (['LandingPageArticle', 'LandingPageFeatureApp', 'LandingPagePrivateBlogPost'].includes(rawModule) && rawAction === 'Create') {
                    rawAction = 'Add New';
                }
                if (['LandingPageAboutUs', 'LandingPageHome', 'LandingPageTerms'].includes(rawModule) && rawAction === 'Create') {
                    rawAction = 'Add New & Edit';
                }
                if (rawModule === 'Appointment' && rawAction === 'Create') {
                    rawAction = 'Add New';
                }
                if (rawModule === 'SystemJob') {
                    if (rawAction === 'View') rawAction = 'View Detail';
                    if (rawAction === 'Edit') rawAction = 'Enable/Disable';
                }
                if (rawModule === 'SmtpSettings' && rawAction === 'Edit') {
                    rawAction = 'Save Configuration & Test Connection';
                }
                if (rawModule === 'AiServicesMasterData' && rawAction === 'Create') {
                    rawAction = 'Add New & Edit';
                }
                if (rawModule === 'AuditLog' && rawAction === 'Delete') {
                    rawAction = 'Delete (Filter Data & Selected)';
                }
                if (rawModule === 'SystemException' && rawAction === 'Delete') {
                    rawAction = 'Delete Selected';
                }
                if (rawModule === 'ChatbotManagement' && rawAction === 'Create') {
                    rawAction = 'Add New';
                }
                if (rawModule === 'QdrantSync' && rawAction === 'Compare') {
                    rawAction = 'Compare Data';
                }
                displayAction = `${rawAction} ${leafNodeName}`;
            } else {
                displayAction = `Manage ${leafNodeName}`;
            }
            
            let current = root;
            for (let i = 0; i < path.length; i++) {
                const nodeName = path[i];
                if (i === path.length - 1) {
                    if (!current[nodeName]) current[nodeName] = { _isTab: true, perms: [] };
                    current[nodeName].perms.push({ id: p, action: displayAction });
                } else {
                    if (!current[nodeName]) current[nodeName] = {};
                    current = current[nodeName];
                }
            }
        });
        
        return root;
    }, [allPermissions]);

    const renderNode = (nodeName, nodeContent, pathPath, level = 0) => {
        if (nodeContent._isTab) {
            const perms = nodeContent.perms;
            const accessPerm = perms.find(p => p.id.endsWith('.Access'));
            const actionPerms = perms.filter(p => !p.id.endsWith('.Access'));
            
            // Checkbox at group level toggles all actions + access
            const allChecked = perms.every(p => rolePermissions.includes(p.id));
            const someChecked = perms.some(p => rolePermissions.includes(p.id));
            const isIndeterminate = someChecked && !allChecked;
            const isOpen = openGroups[pathPath] !== false;
            
            return (
                <div className="tree-group mb-2" key={pathPath} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                    <div className="d-flex align-items-center" style={{ padding: '4px 0' }}>
                        <div 
                            onClick={() => toggleGroup(pathPath)} 
                            style={{ cursor: 'pointer', width: '20px', display: 'flex', justifyContent: 'center' }}
                        >
                            <FontAwesomeIcon 
                                icon={isOpen ? faChevronDown : faChevronRight} 
                                className="text-muted" 
                                style={{ fontSize: '0.85rem' }} 
                            />
                        </div>
                        <div className="form-check m-0 ms-1 d-flex align-items-center">
                            <input 
                                className="form-check-input mt-0 me-2 shadow-sm" 
                                type="checkbox" 
                                id={`group-${pathPath}`}
                                checked={allChecked}
                                ref={input => {
                                    if (input) input.indeterminate = isIndeterminate;
                                }}
                                onChange={(e) => handleToggleGroup(perms, e.target.checked)}
                                disabled={loading}
                                style={{ cursor: 'pointer' }}
                            />
                            <label 
                                className="form-check-label fw-bold" 
                                htmlFor={`group-${pathPath}`} 
                                style={{ cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem', color: '#2f5aa2' }}
                            >
                                {nodeName}
                            </label>
                        </div>
                    </div>
                    
                    {isOpen && (
                        <div className="tree-children ms-4 mt-2 mb-3">
                            {/* Access Menu Toggle */}
                            {accessPerm && (
                                <div className="form-check mb-2 pb-2" style={{ borderBottom: '1px dashed #e8eaed' }}>
                                    <input 
                                        className="form-check-input shadow-sm mt-0" 
                                        type="checkbox" 
                                        id={accessPerm.id}
                                        checked={rolePermissions.includes(accessPerm.id)}
                                        onChange={() => handleTogglePermission(accessPerm.id)}
                                        disabled={loading}
                                        style={{ cursor: 'pointer', transform: 'translateY(2px)' }}
                                    />
                                    <label 
                                        className="form-check-label fs-9 ms-2 fw-bold text-primary" 
                                        htmlFor={accessPerm.id} 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                    >
                                        Enable Screen Access (Show in Menu{nodeName === 'Image Gallery' ? ', Download, Copy Link' : ''})
                                    </label>
                                </div>
                            )}

                            {/* Action Permissions */}
                            {actionPerms.length > 0 && (
                                <div className="d-flex flex-column gap-1 ms-2">
                                    <div className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>AVAILABLE ACTIONS</div>
                                    {actionPerms.map(p => {
                                        const isAccessGranted = accessPerm ? rolePermissions.includes(accessPerm.id) : true;
                                        return (
                                        <div className="form-check m-0" key={p.id}>
                                            <input 
                                                className="form-check-input shadow-sm mt-0" 
                                                type="checkbox" 
                                                id={p.id}
                                                checked={rolePermissions.includes(p.id)}
                                                onChange={() => handleTogglePermission(p.id)}
                                                disabled={loading || !isAccessGranted}
                                                style={{ cursor: (!isAccessGranted) ? 'not-allowed' : 'pointer', transform: 'translateY(2px)' }}
                                            />
                                            <label 
                                                className="form-check-label fs-9 ms-2 text-dark" 
                                                htmlFor={p.id} 
                                                style={{ cursor: (!isAccessGranted) ? 'not-allowed' : 'pointer', userSelect: 'none', opacity: (!isAccessGranted) ? 0.5 : 1 }}
                                            >
                                                {p.action}
                                            </label>
                                        </div>
                                    )})}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        } else {
            const isOpen = openGroups[pathPath] !== false; 
            
            const gatherPerms = (obj) => {
                let pList = [];
                Object.values(obj).forEach(val => {
                    if (val._isTab) pList.push(...val.perms);
                    else pList.push(...gatherPerms(val));
                });
                return pList;
            };
            const allNestedPerms = gatherPerms(nodeContent);
            if (allNestedPerms.length === 0) return null;

            const allChecked = allNestedPerms.every(p => rolePermissions.includes(p.id));
            const someChecked = allNestedPerms.some(p => rolePermissions.includes(p.id));
            const isIndeterminate = someChecked && !allChecked;

            return (
                <div className="tree-folder mb-3" key={pathPath} style={level === 0 ? { breakInside: 'avoid', pageBreakInside: 'avoid' } : {}}>
                    <div className="d-flex align-items-center" style={{ padding: '6px 0', backgroundColor: level === 0 ? '#fcfcfc' : 'transparent', borderRadius: level === 0 ? '4px' : '0' }}>
                        <div 
                            onClick={() => toggleGroup(pathPath)} 
                            style={{ cursor: 'pointer', width: '20px', display: 'flex', justifyContent: 'center' }}
                        >
                            <FontAwesomeIcon 
                                icon={isOpen ? faChevronDown : faChevronRight} 
                                className="text-dark" 
                                style={{ fontSize: '0.9rem' }} 
                            />
                        </div>
                        <div className="form-check m-0 ms-1 d-flex align-items-center">
                            <input 
                                className="form-check-input mt-0 me-2 shadow-sm" 
                                type="checkbox" 
                                id={`folder-${pathPath}`}
                                checked={allChecked}
                                ref={input => {
                                    if (input) input.indeterminate = isIndeterminate;
                                }}
                                onChange={(e) => handleToggleGroup(allNestedPerms, e.target.checked)}
                                disabled={loading}
                                style={{ cursor: 'pointer' }}
                            />
                            <label 
                                className="form-check-label fw-bold text-dark" 
                                htmlFor={`folder-${pathPath}`} 
                                style={{ cursor: 'pointer', userSelect: 'none', fontSize: level === 0 ? '1rem' : '0.95rem' }}
                            >
                                {nodeName}
                            </label>
                        </div>
                    </div>
                    
                    {isOpen && (
                        <div className="tree-children ms-4 mt-2">
                            {Object.entries(nodeContent).map(([childName, childContent]) => 
                                renderNode(childName, childContent, `${pathPath}-${childName}`, level + 1)
                            )}
                        </div>
                    )}
                </div>
            );
        }
    };

    const handleClose = () => {
        if (onClose) onClose();
    };

    return (
        <>
        <CenteredModal
            show={show}
            onClose={handleClose}
            title="Role & Permission Management"
            confirmText="Save Permissions"
            size="xl"
            modalType="modal-xl"
            formId="formRoleManagement"
            isSubmitting={loading}
            submittingText="Saving..."
            disabledConfirmBtn={!selectedRole || selectedRole.Name === 'Super Admin' || selectedRole.Name === 'User' || !selectedUserId}
        >
            <div className="pb-0">
                {message && (
                    <div className={messageType === 'success' ? "auth-success-message mx-3 mt-3 mb-3" : `alert alert-${messageType} py-2 fs-9 d-flex align-items-center mx-3 mb-3 mt-3`} role="alert">
                        {messageType === 'danger' && <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />}
                        {messageType === 'success' && <FontAwesomeIcon icon={faCheck} className="me-2" style={{ color: '#16a34a' }} />}
                        <span style={messageType === 'success' ? { color: '#166534', fontWeight: 500, fontSize: '0.85rem' } : {}}>{message}</span>
                    </div>
                )}

                <form id="formRoleManagement" onSubmit={handleSubmit}>
                    <div className="d-flex" style={{ borderTop: '1px solid #e8eaed', height: 'calc(100vh - 220px)', minHeight: '450px' }}>
                        
                        {/* LEFT PANE - ROLES */}
                        <div style={{ width: '280px', minWidth: '280px', borderRight: '1px solid #e8eaed', display: 'flex', flexDirection: 'column', background: '#fafbfc' }}>
                            {/* Roles Header */}
                            <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#5f6368' }}>
                                    Roles ({roles.length})
                                </span>
                            </div>

                            {/* Roles List */}
                            <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px' }}>
                                {roles.map(role => {
                                    const isSelected = selectedRole?.Id === role.Id;
                                    const isSuperAdmin = role.Name === 'Super Admin';
                                    return (
                                        <div 
                                            key={role.Id}
                                            onClick={() => handleSelectRole(role)}
                                            style={{
                                                padding: '8px 12px',
                                                marginBottom: '6px',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                backgroundColor: isSelected ? '#1a73e8' : '#fff',
                                                color: isSelected ? '#fff' : '#333',
                                                border: isSelected ? '1px solid #1a73e8' : '1px solid #e8eaed',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                boxShadow: isSelected ? '0 2px 8px rgba(26,115,232,0.3)' : '0 1px 3px rgba(0,0,0,0.04)',
                                            }}
                                            onMouseEnter={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = '#1a73e8'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(26,115,232,0.12)'; }}}
                                            onMouseLeave={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = '#e8eaed'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: '28px', height: '28px', borderRadius: '6px',
                                                    background: isSelected ? 'rgba(255,255,255,0.2)' : (isSuperAdmin ? '#fef7e0' : '#e8f0fe'),
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <FontAwesomeIcon 
                                                        icon={faShieldAlt} 
                                                        style={{ 
                                                            fontSize: '0.75rem',
                                                            color: isSelected ? '#fff' : (isSuperAdmin ? '#f9ab00' : '#1a73e8')
                                                        }} 
                                                    />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.2 }}>{role.Name}</div>
                                                    {isSuperAdmin && (
                                                        <div style={{ fontSize: '0.7rem', color: isSelected ? 'rgba(255,255,255,0.8)' : '#888', marginTop: '1px' }}>Full access</div>
                                                    )}
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.7rem', opacity: 0.7 }} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                        </div>

                        {/* RIGHT PANE - PERMISSIONS */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', minWidth: 0 }}>
                            {/* Permissions Header */}
                            <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8eaed', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h6 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#202124' }}>
                                        {selectedRole ? `${selectedRole.Name}` : 'Permissions'}
                                    </h6>
                                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#80868b', marginTop: '2px' }}>
                                        {selectedRole 
                                            ? (selectedRole.Name === 'Super Admin' 
                                                ? 'Super Admin has implicit access to all permissions.' 
                                                : 'Manage granular permissions for this role.')
                                            : 'Select a role to configure permissions.'}
                                    </p>
                                </div>
                                {selectedRole && selectedRole.Name !== 'Super Admin' && (
                                    <div 
                                        onClick={() => setShowUserPicker(true)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                            cursor: loading ? 'not-allowed' : 'pointer',
                                            padding: '8px 14px',
                                            border: '1px solid #dadce0',
                                            borderRadius: '8px',
                                            backgroundColor: '#fff',
                                            transition: 'all 0.2s',
                                            minWidth: '200px'
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1a73e8'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(26,115,232,0.15)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#dadce0'; e.currentTarget.style.boxShadow = 'none'; }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.7rem', color: '#80868b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Apply to</div>
                                            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#202124', marginTop: '1px' }}>
                                                {(() => {
                                                    if (!selectedUserId) return <span style={{ color: '#d93025' }}>Select a User</span>;
                                                    const u = usersInRole.find(usr => usr.Id === selectedUserId);
                                                    if (!u) return 'User';
                                                    return (
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            {u.UserName}
                                                            <span style={{ color: '#80868b', fontWeight: 400, fontSize: '0.75rem' }}>({u.Email})</span>
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '0.7rem', color: '#80868b' }} />
                                    </div>
                                )}
                            </div>

                            {/* Permissions Content */}
                            <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto', opacity: selectedRole ? 1 : 0.4, pointerEvents: selectedRole ? 'auto' : 'none' }}>
                                {selectedRole ? (
                                    selectedRole.Name === 'Super Admin' ? (
                                        <div className="text-center" style={{ paddingTop: '80px' }}>
                                            <div style={{ 
                                                width: '72px', height: '72px', borderRadius: '50%', 
                                                background: 'linear-gradient(135deg, #fef7e0, #fff3cd)', 
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                marginBottom: '20px'
                                            }}>
                                                <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '1.8rem', color: '#f9ab00' }} />
                                            </div>
                                            <h5 style={{ fontWeight: 700, color: '#202124', marginBottom: '8px' }}>Full System Access</h5>
                                            <p style={{ color: '#80868b', fontSize: '0.9rem', maxWidth: '380px', margin: '0 auto' }}>
                                                The Super Admin role inherently possesses all permissions. Individual permissions cannot be modified.
                                            </p>
                                        </div>
                                    ) : selectedRole.Name === 'User' ? (
                                        <div className="text-center" style={{ paddingTop: '80px' }}>
                                            <div style={{ 
                                                width: '72px', height: '72px', borderRadius: '50%', 
                                                background: 'linear-gradient(135deg, #e8f0fe, #d2e3fc)', 
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                marginBottom: '20px'
                                            }}>
                                                <FontAwesomeIcon icon={faUser} style={{ fontSize: '1.8rem', color: '#1a73e8' }} />
                                            </div>
                                            <h5 style={{ fontWeight: 700, color: '#202124', marginBottom: '8px' }}>Standard User Access</h5>
                                            <p style={{ color: '#80868b', fontSize: '0.9rem', maxWidth: '420px', margin: '0 auto', lineHeight: '1.5' }}>
                                                The <b>User</b> role is assigned to standard application users who do not have access to the Administrator panel. Granular administrative permissions are not applicable.
                                            </p>
                                        </div>
                                    ) : !selectedUserId ? (
                                        <div className="text-center" style={{ paddingTop: '80px' }}>
                                            <div style={{ 
                                                width: '72px', height: '72px', borderRadius: '50%', 
                                                background: '#f8f9fa', 
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                marginBottom: '20px', border: '1px dashed #dadce0'
                                            }}>
                                                <FontAwesomeIcon icon={faUser} style={{ fontSize: '1.8rem', color: '#9aa0a6' }} />
                                            </div>
                                            <h6 style={{ fontWeight: 600, color: '#5f6368', marginBottom: '8px' }}>Please select a user to begin</h6>
                                            <p style={{ color: '#9aa0a6', fontSize: '0.85rem' }}>
                                                Click the "Apply to" dropdown in the top right corner to pick a user and configure their permissions.
                                            </p>
                                        </div>
                                    ) : (
                                        (() => {
                                            const rootKeys = Object.keys(groupedPermissions);
                                            const half = Math.ceil(rootKeys.length / 2);
                                            const leftKeys = rootKeys.slice(0, half);
                                            const rightKeys = rootKeys.slice(half);

                                            const renderColumn = (keys) => (
                                                <div className="d-flex flex-column gap-1">
                                                    {keys.map(k => renderNode(k, groupedPermissions[k], k, 0))}
                                                </div>
                                            );

                                            return (
                                                <div className="row m-0 tree-view-container">
                                                    <div className="col-md-6 border-end pe-4">
                                                        {renderColumn(leftKeys)}
                                                    </div>
                                                    <div className="col-md-6 ps-4">
                                                        {renderColumn(rightKeys)}
                                                    </div>
                                                </div>
                                            );
                                        })()
                                    )
                                ) : (
                                    <div className="text-center" style={{ paddingTop: '80px' }}>
                                        <div style={{ 
                                            width: '72px', height: '72px', borderRadius: '50%', 
                                            background: '#f1f3f4', 
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            marginBottom: '20px'
                                        }}>
                                            <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '1.8rem', color: '#9aa0a6' }} />
                                        </div>
                                        <h6 style={{ fontWeight: 600, color: '#5f6368', marginBottom: '8px' }}>No role selected</h6>
                                        <p style={{ color: '#9aa0a6', fontSize: '0.85rem' }}>Choose a role from the sidebar to manage its permissions.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </form>
            </div>
        </CenteredModal>

        {/* USER PICKER MODAL */}
        <CenteredModal
            show={showUserPicker}
            onClose={() => setShowUserPicker(false)}
            title={`Select User (Role: ${selectedRole?.Name})`}
            size="lg"
            modalType="modal-lg"
            closeText="Close"
            zIndex={1100}
        >
            <div className="p-2">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="text-muted fs-9 m-0">
                        Pick a specific user to view or override their individual permissions.
                    </p>
                </div>
                
                <div className="table-responsive border rounded">
                    <table className="table table-hover table-sm fs-9 m-0">
                        <thead className="table-light">
                            <tr>
                                <th className="py-2 px-3">UserName</th>
                                <th className="py-2 px-3">Email</th>
                                <th className="py-2 px-3 text-center">Status</th>
                                <th className="py-2 px-3 text-center" style={{ width: '100px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {usersInRole.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="text-center text-muted py-4">No users found in this role.</td>
                                </tr>
                            ) : (
                                usersInRole.map(u => (
                                    <tr key={u.Id} className={selectedUserId === u.Id ? 'table-primary' : ''}>
                                        <td className="align-middle fw-bold px-3">{u.UserName}</td>
                                        <td className="align-middle px-3 text-muted">{u.Email}</td>
                                        <td className="align-middle text-center px-3">
                                            {u.IsLocked 
                                                ? <span className="badge bg-danger">Locked</span>
                                                : <span className="badge bg-success">Active</span>
                                            }
                                        </td>
                                        <td className="align-middle text-center px-3">
                                            <button 
                                                type="button"
                                                className={`btn btn-sm py-1 px-3 fs-9 ${selectedUserId === u.Id ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                disabled={u.IsLocked}
                                                onClick={() => {
                                                    handleSelectUser(u.Id);
                                                    setShowUserPicker(false);
                                                }}
                                            >
                                                {selectedUserId === u.Id ? 'Selected' : 'Pick'}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </CenteredModal>
        </>
    );
}

export default RoleManagementModal;
